import { useCallback } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { appCopy, type ActionFailureBody, type ActionInput, type ActionName, type ActionOutput, type ActionResponse } from '@zibanu/shared';
import { useDeps } from './deps';
import { useBooks, type Membership } from './session';
import { useNotices } from './notices';

/** A refusal, carried so a screen can say why ("this one matches your bank alert"). */
export class ActionError extends Error {
  constructor(readonly body: ActionFailureBody) {
    super(body.reason ?? body.error);
    this.name = 'ActionError';
  }
}

/** Everything about one set of books starts with its id: switching books is a fresh start, and losing them wipes exactly them. */
export const booksKey = (orgId: string, name: string, input?: unknown) => (input === undefined ? [orgId, name] : [orgId, name, input]);

/**
 * Access to these books ended (removed, or the books went): say so, wipe
 * their cached data and queued captures from this phone, and let /me move
 * the app to other books (spec 3A §6.2, Review Focus 5).
 */
export function useLostBooks() {
  const { queryClient, outbox } = useDeps();
  const { show } = useNotices();
  return useCallback(
    async (books: Membership) => {
      show(appCopy.noLongerOn(books.label));
      queryClient.removeQueries({ queryKey: [books.orgId] });
      // A failed wipe step never strands the person on dead books: moving on runs last, whatever happened.
      await outbox.clearOrg(books.orgId).catch(() => undefined);
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    [outbox, queryClient, show],
  );
}

/** Call an action in the books on screen. A not_found for the books themselves means access ended. */
export function useBooksCall() {
  const { client } = useDeps();
  const { current } = useBooks();
  const lost = useLostBooks();
  return useCallback(
    async <N extends ActionName>(name: N, input: ActionInput<N>): Promise<ActionResponse<ActionOutput<N>>> => {
      if (!current) return { ok: false, error: 'not_found', reason: 'org' };
      const res = await client.call(name, input, current.orgId);
      if (!res.ok && res.error === 'not_found' && res.reason === 'org') await lost(current);
      return res;
    },
    [client, current, lost],
  );
}

/** Read an action's answer for the books on screen, cached under their id. */
export function useBooksQuery<N extends ActionName>(
  name: N,
  input: ActionInput<N>,
  options: Omit<UseQueryOptions<ActionOutput<N>, Error>, 'queryKey' | 'queryFn'> = {},
) {
  const call = useBooksCall();
  const { current } = useBooks();
  return useQuery<ActionOutput<N>, Error>({
    ...options,
    queryKey: booksKey(current?.orgId ?? '', name, input),
    queryFn: async () => {
      const res = await call(name, input);
      if (!res.ok) throw new ActionError(res);
      return res.data;
    },
    // Never without books, whatever the caller asks.
    enabled: Boolean(current) && (options.enabled ?? true),
  });
}
/**
 * Records' pages through entries.list's cursor (spec 3A §6.3). Its own key
 * under the books' id, so a switch or a lost set of books clears it with
 * everything else.
 */
export function useEntryPages(input: Omit<ActionInput<'entries.list'>, 'cursor'>) {
  const call = useBooksCall();
  const { current } = useBooks();
  return useInfiniteQuery({
    queryKey: [current?.orgId ?? '', 'entries.pages', input],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const res = await call('entries.list', pageParam ? { ...input, cursor: pageParam } : input);
      if (!res.ok) throw new ActionError(res);
      return res.data;
    },
    getNextPageParam: (last) => last.nextCursor,
    enabled: Boolean(current),
  });
}

/** The customers list through its cursor; `owing` and the total come with the first page only (spec 3B §8.1). */
export function useCustomerPages(input: Omit<ActionInput<'customers.list'>, 'cursor'>) {
  const call = useBooksCall();
  const { current } = useBooks();
  return useInfiniteQuery({
    queryKey: [current?.orgId ?? '', 'customers.list', input],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const res = await call('customers.list', pageParam ? { ...input, cursor: pageParam } : input);
      if (!res.ok) throw new ActionError(res);
      return res.data;
    },
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    enabled: Boolean(current),
  });
}

/** One customer, and their bought-and-paid list through its cursor (spec 3B §8.2). */
export function useCustomerHistory(counterpartyId: string) {
  const call = useBooksCall();
  const { current } = useBooks();
  return useInfiniteQuery({
    queryKey: [current?.orgId ?? '', 'customer.get', { counterpartyId }],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const res = await call('customer.get', pageParam ? { counterpartyId, cursor: pageParam } : { counterpartyId });
      if (!res.ok) throw new ActionError(res);
      return res.data;
    },
    getNextPageParam: (last) => last.nextCursor,
    enabled: Boolean(current),
  });
}
